/**
 * Cliente de las tres rutas que cierran el flujo de reservación:
 *
 *   POST /api/reservaciones              — reservar un bloque
 *   POST /api/reservaciones/:id/cancelar — cancelar una propia
 *   GET  /api/mis-reservaciones          — el historial completo
 *
 * LECTURA ESTRICTA, como en `disponibilidad.ts` y por la misma razón: el
 * contrato está escrito. Los ayudantes viven en `contrato.ts`.
 *
 * ═══ LAS DOS DECISIONES QUE NO SON OBVIAS ═══
 *
 * 1. EL TEXTO DE `error` DEL SERVIDOR NUNCA SE SUSTITUYE. Los mensajes de
 *    `apps/api` ya vienen redactados para que los lea un alumno. El `motivo`
 *    del 409 NO es un texto: es un código estable que sirve para decidir qué
 *    OFRECER además del mensaje —un enlace a «Mis reservaciones» cuando ya
 *    tiene una activa, una invitación a elegir otro bloque cuando no hay
 *    cupo—. Por eso el conflicto viaja en su propia clase de error, con el
 *    mensaje intacto y el motivo al lado.
 *
 *    Y por eso `motivo` puede ser `null` sin que eso rompa nada: si el backend
 *    algún día agrega un código que este cliente no conoce, lo que NO se puede
 *    perder es el mensaje. Se muestra el texto del servidor y simplemente no se
 *    ofrece ningún atajo. Descartar la respuesta entera por un código nuevo
 *    convertiría un conflicto explicado en un «error inesperado».
 *
 * 2. UNA CANCELACIÓN QUE EL SERVIDOR ACEPTÓ NO SE PUEDE REPORTAR COMO FALLA.
 *    Con un 200 en la mano, la reservación YA está cancelada en la base. Si
 *    aquí se exigiera el `detalle` con la misma dureza que se exige el cuerpo
 *    de una reservación, una divergencia cosmética pintaría un error rojo sobre
 *    una operación que sí ocurrió, y quien lo lea intentará cancelar otra vez.
 *    Por eso `detalle` se lee como texto opcional. En `reservar` sí se exige
 *    todo, porque ahí los datos son lo que se muestra en la confirmación.
 */

import {
  ErrorApi,
  describirFalloDeRed,
  esRegistro,
  leerJson,
  mensajeDelCuerpo,
} from './errores'
import {
  exigirEnteroPositivo,
  exigirIdentificador,
  exigirInstante,
  exigirLista,
  exigirOpcion,
  exigirRegistro,
  exigirTexto,
} from './contrato'

const TIEMPO_LIMITE_LECTURA_MS = 8000
const TIEMPO_LIMITE_ESCRITURA_MS = 10000

/**
 * Clave de TanStack Query del historial. Se exporta para poder invalidarla
 * desde la pantalla de disponibilidad al reservar: quien acaba de reservar y
 * entra a «Mis reservaciones» tiene que ver la nueva, no la lista de antes.
 */
export const CLAVE_MIS_RESERVACIONES = ['mis-reservaciones'] as const

/* --- Tipos del contrato --------------------------------------------------- */

/**
 * Códigos de `motivo` del 409. Son estables y es lo que decide qué se ofrece;
 * el texto que se lee viene en `error`.
 */
export type MotivoConflicto =
  | 'sin_cupo'
  | 'espacio_bloqueado'
  | 'excede_duracion'
  | 'ya_tiene_activa'
  | 'fuera_de_horario'
  | 'dia_no_habil'

const MOTIVOS: readonly MotivoConflicto[] = [
  'sin_cupo',
  'espacio_bloqueado',
  'excede_duracion',
  'ya_tiene_activa',
  'fuera_de_horario',
  'dia_no_habil',
]

/**
 * Los cinco estados que puede tener una reservación de alumno. Son cinco de los
 * seis valores del ENUM `estado_ocupacion` de la migración 001; el sexto,
 * `bloqueo`, no es una reservación —es un bloqueo administrativo sin usuario—
 * y por eso no puede aparecer en `/api/mis-reservaciones`.
 */
export type EstadoReservacion =
  | 'confirmada'
  | 'en_curso'
  | 'completada'
  | 'cancelada'
  | 'no_asistio'

const ESTADOS: readonly EstadoReservacion[] = [
  'confirmada',
  'en_curso',
  'completada',
  'cancelada',
  'no_asistio',
]

/** Lo que devuelve un 201 de `POST /api/reservaciones`. */
export interface ReservacionCreada {
  /** `id` */
  id: string
  /** `espacio_id`, por ejemplo `CUB-01`. */
  espacioId: string
  /** `lugar`: número de lugar dentro del espacio, desde 1. */
  lugar: number
  /** `inicio`: instante UTC ya convertido. Se muestra en la zona del sistema. */
  inicio: Date
  /** `fin` */
  fin: Date
}

/** Una fila de `GET /api/mis-reservaciones`. */
export interface MiReservacion {
  /** `id` */
  id: string
  /** `espacio_id` */
  espacioId: string
  /** `espacio_nombre` */
  espacioNombre: string
  /** `lugar` */
  lugar: number
  /** `inicio` */
  inicio: Date
  /** `fin` */
  fin: Date
  /** `estado` */
  estado: EstadoReservacion
}

/** Cuerpo de `POST /api/reservaciones`. Los instantes viajan en ISO 8601 UTC. */
export interface PeticionReservar {
  /** Clave del espacio. */
  espacio: string
  inicio: Date
  fin: Date
}

/* --- Errores con forma ---------------------------------------------------- */

/**
 * El 409. Lleva el mensaje del servidor intacto y, al lado, el código que
 * decide qué ofrecer. `motivo` es `null` cuando el código no está en la lista
 * que este cliente conoce: ver la decisión 1 del encabezado.
 */
export class ErrorDeConflicto extends ErrorApi {
  readonly motivo: MotivoConflicto | null

  constructor(mensaje: string, motivo: MotivoConflicto | null) {
    super(409, mensaje)
    this.name = 'ErrorDeConflicto'
    this.motivo = motivo
  }
}

/**
 * El 403 con `estado: "sin_registrar"`: hay sesión de Google, pero no hay fila
 * en `usuarios` (ADR-0006, decisión 2). La pantalla lo evita de antemano, pero
 * puede llegar igual si el registro se perdió entre la carga y el clic.
 */
export class ErrorSinRegistrar extends ErrorApi {
  constructor(mensaje: string) {
    super(403, mensaje)
    this.name = 'ErrorSinRegistrar'
  }
}

/* --- Peticiones ----------------------------------------------------------- */

/**
 * `POST /api/reservaciones`.
 *
 * Lanza `ErrorDeConflicto` en 409, `ErrorSinRegistrar` en el 403 de registro
 * faltante, `ErrorApi` en el resto de códigos y `ErrorDeContrato` si el 201 no
 * trae lo que promete.
 */
export async function reservar(
  peticion: PeticionReservar,
): Promise<ReservacionCreada> {
  let respuesta: Response
  try {
    respuesta = await fetch('/api/reservaciones', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        accept: 'application/json',
      },
      // La cookie de sesión de better-auth es de este mismo origen (ADR-0006).
      credentials: 'same-origin',
      body: JSON.stringify({
        espacio: peticion.espacio,
        inicio: peticion.inicio.toISOString(),
        fin: peticion.fin.toISOString(),
      }),
      signal: AbortSignal.timeout(TIEMPO_LIMITE_ESCRITURA_MS),
    })
  } catch (error) {
    throw new Error(
      `No se pudo enviar la reservación (${describirFalloDeRed(error)}). ` +
        'Nada quedó reservado; puedes intentarlo de nuevo.',
    )
  }

  const cuerpo = await leerJson(respuesta)

  if (!respuesta.ok) {
    throw errorDeRespuesta(respuesta.status, cuerpo, '/api/reservaciones')
  }

  const raiz = exigirRegistro(cuerpo, 'la respuesta')
  // Se comprueba aunque no se use: un 201 con otro estado significaría que el
  // contrato cambió, y es mejor decirlo que confirmar algo distinto de lo que
  // se guardó.
  exigirOpcion(raiz, 'estado', ['confirmada'] as const, 'la respuesta')

  return leerReservacionCreada(raiz['reservacion'])
}

/**
 * `POST /api/reservaciones/:id/cancelar`.
 *
 * No se pide motivo al alumno: el backend pone uno por omisión, y la columna
 * `motivo` es obligatoria al cancelar por el CHECK `cancelacion_con_motivo`.
 *
 * EL MOTIVO SÍ SE MANDA CUANDO CANCELA UN ADMINISTRADOR. La función `cancelar()`
 * de la migración 008 le permite cancelar reservaciones ajenas en cualquier
 * momento (ESPAC-16), y ahí el motivo importa: queda en `ocupaciones.motivo` y
 * en la auditoría, y es lo único que después explica por qué desapareció la
 * reservación de alguien. Lo usa el panel de administración al resolver los
 * conflictos de un 409.
 *
 * Devuelve el `detalle` del servidor, o `null` si no vino. Un 200 significa que
 * la cancelación ya ocurrió: ver la decisión 2 del encabezado.
 */
export async function cancelarReservacion(
  id: string,
  motivo?: string,
): Promise<string | null> {
  const ruta = `/api/reservaciones/${encodeURIComponent(id)}/cancelar`
  // Un motivo en blanco se omite: el backend pone el suyo por omisión, y
  // mandar una cadena vacía solo serviría para que la rechazara.
  const cuerpoEnviado =
    motivo !== undefined && motivo.trim() !== ''
      ? JSON.stringify({ motivo: motivo.trim() })
      : '{}'

  let respuesta: Response
  try {
    respuesta = await fetch(ruta, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        accept: 'application/json',
      },
      credentials: 'same-origin',
      body: cuerpoEnviado,
      signal: AbortSignal.timeout(TIEMPO_LIMITE_ESCRITURA_MS),
    })
  } catch (error) {
    throw new Error(
      `No se pudo enviar la cancelación (${describirFalloDeRed(error)}). ` +
        'Tu reservación sigue como estaba; puedes intentarlo de nuevo.',
    )
  }

  const cuerpo = await leerJson(respuesta)

  if (!respuesta.ok) {
    throw errorDeRespuesta(respuesta.status, cuerpo, ruta)
  }

  if (!esRegistro(cuerpo)) return null
  const detalle = cuerpo['detalle']
  return typeof detalle === 'string' && detalle.trim() !== '' ? detalle : null
}

/**
 * `GET /api/mis-reservaciones`. Devuelve TODOS los estados: es el historial, no
 * solo lo vigente.
 */
export async function consultarMisReservaciones(): Promise<MiReservacion[]> {
  let respuesta: Response
  try {
    respuesta = await fetch('/api/mis-reservaciones', {
      headers: { accept: 'application/json' },
      credentials: 'same-origin',
      signal: AbortSignal.timeout(TIEMPO_LIMITE_LECTURA_MS),
    })
  } catch (error) {
    throw new Error(
      `No se pudieron consultar tus reservaciones (${describirFalloDeRed(error)}).`,
    )
  }

  const cuerpo = await leerJson(respuesta)

  if (!respuesta.ok) {
    throw errorDeRespuesta(respuesta.status, cuerpo, '/api/mis-reservaciones')
  }

  const raiz = exigirRegistro(cuerpo, 'la respuesta')
  return exigirLista(raiz, 'reservaciones', 'la respuesta').map((fila, indice) =>
    leerMiReservacion(fila, `reservaciones[${indice}]`),
  )
}

/* --- Interpretación ------------------------------------------------------- */

/**
 * Convierte una respuesta no exitosa en el error con la forma que la pantalla
 * necesita. El mensaje del servidor pasa intacto en todos los casos.
 */
function errorDeRespuesta(
  estadoHttp: number,
  cuerpo: unknown,
  ruta: string,
): Error {
  const mensaje =
    mensajeDelCuerpo(cuerpo) ?? `El servidor respondió ${estadoHttp} en ${ruta}.`

  if (estadoHttp === 409) {
    return new ErrorDeConflicto(mensaje, leerMotivo(cuerpo))
  }

  if (estadoHttp === 403 && leerEstadoDelCuerpo(cuerpo) === 'sin_registrar') {
    return new ErrorSinRegistrar(mensaje)
  }

  return new ErrorApi(estadoHttp, mensaje)
}

/** `null` cuando el código no está en la lista conocida. No lanza a propósito. */
function leerMotivo(cuerpo: unknown): MotivoConflicto | null {
  if (!esRegistro(cuerpo)) return null
  const valor = cuerpo['motivo']
  if (typeof valor !== 'string') return null
  return MOTIVOS.find((motivo) => motivo === valor) ?? null
}

function leerEstadoDelCuerpo(cuerpo: unknown): string | null {
  if (!esRegistro(cuerpo)) return null
  const valor = cuerpo['estado']
  return typeof valor === 'string' ? valor : null
}

function leerReservacionCreada(valor: unknown): ReservacionCreada {
  const ruta = 'la respuesta.reservacion'
  const registro = exigirRegistro(valor, ruta)

  return {
    id: exigirIdentificador(registro, 'id', ruta),
    espacioId: exigirTexto(registro, 'espacio_id', ruta),
    lugar: exigirEnteroPositivo(registro, 'lugar', ruta),
    inicio: exigirInstante(registro, 'inicio', ruta),
    fin: exigirInstante(registro, 'fin', ruta),
  }
}

function leerMiReservacion(fila: unknown, ruta: string): MiReservacion {
  const registro = exigirRegistro(fila, ruta)

  return {
    id: exigirIdentificador(registro, 'id', ruta),
    espacioId: exigirTexto(registro, 'espacio_id', ruta),
    espacioNombre: exigirTexto(registro, 'espacio_nombre', ruta),
    lugar: exigirEnteroPositivo(registro, 'lugar', ruta),
    inicio: exigirInstante(registro, 'inicio', ruta),
    fin: exigirInstante(registro, 'fin', ruta),
    estado: exigirOpcion(registro, 'estado', ESTADOS, ruta),
  }
}
